/**
 * Authentication Utilities
 * 
 * Security Features:
 * - JWT token validation with server (/api/users/validate)
 * - Automatic logout on 401 responses
 * - Token included in Authorization header for protected endpoints
 * - Periodic token validation (every 5 minutes via useAuth hook)
 * - Automatic token cleanup on validation failure
 * - Uses secure cookies with js-cookie library
 * 
 * Usage:
 * - Use loginUser/registerUser for authentication
 * - Use fetchWithAuth for all protected API calls
 * - Use useAuth hook in components for auth state management
 * - Token and user data persisted in cookies
 * 
 * Security Considerations:
 * - Cookies persist across page refreshes and browser sessions
 * - SameSite=Lax protects against CSRF attacks
 * - Secure flag ensures HTTPS-only transmission in production
 * - Always use HTTPS in production
 * - Token validation prevents use of expired/invalid tokens
 */

import { googleLogout } from '@react-oauth/google';
import Cookies from 'js-cookie';

interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

interface RegisterResponse {
  token: string;
  id: string;
  email: string;
  name: string;

}

export const loginUser = async (email: string, password: string): Promise<LoginResponse> => {
  const response = await fetch('/api/users/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro ao fazer login');
  }

  return response.json();
};

export const registerUser = async (
  email: string,
  password: string,
  name: string,
  marketingAccepted: boolean
): Promise<RegisterResponse> => {
  const response = await fetch('/api/users/register', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password, name, acceptsMarketing: marketingAccepted }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro ao criar conta');
  }

  return response.json();
};

// Cookie configuration - secure in production, permissive in development
const isProduction = window.location.protocol === 'https:';

export const setAuthToken = (token: string) => {
  Cookies.set('authToken', token, {
    expires: 7, // 7 days
    path: '/',
    secure: isProduction,
    sameSite: 'Lax'
  });
};

export const getAuthToken = (): string | null => {
  return Cookies.get('authToken') || null;
};

export const removeAuthToken = () => {
  Cookies.remove('authToken', { path: '/' });
};

export const setUserData = (user: { id: string; email: string; name: string }) => {
  Cookies.set('userData', JSON.stringify(user), {
    expires: 7,
    path: '/',
    secure: isProduction,
    sameSite: 'Lax'
  });
};

export const getUserData = (): { id: string; email: string; name: string } | null => {
  const data = Cookies.get('userData');
  if (!data) return null;

  try {
    return JSON.parse(data);
  } catch (error) {
    console.error('Failed to parse user data:', error);
    return null;
  }
};

export const removeUserData = () => {
  Cookies.remove('userData', { path: '/' });
};

export const logout = () => {
  googleLogout();
  removeAuthToken();
  removeUserData();
};

export const validateToken = async (): Promise<boolean> => {
  const token = getAuthToken();

  if (!token) {
    return false;
  }

  try {
    const response = await fetch('/api/users/validate', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    // Only logout on authentication errors (401/403)
    // For other errors (500, etc), keep the token and retry later
    if (response.status === 401 || response.status === 403) {
      logout();
      return false;
    }

    if (!response.ok) {
      // Server error or other issue - keep token but return false
      console.warn('Token validation returned non-OK status:', response.status);
      return false;
    }

    return true;
  } catch (error) {
    // Network error or server down, keep token but return false
    console.error('Token validation failed:', error);
    return false;
  }
};

export const fetchWithAuth = async (url: string, options: RequestInit = {}): Promise<Response> => {
  const token = getAuthToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> || {}),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  // If we get a 401, token is invalid - logout user
  if (response.status === 401) {
    logout();
    window.location.href = '/';
  }

  return response;
};

// Example: Get user credits (protected endpoint)
export const getUserCredits = async (): Promise<number> => {
  const response = await fetchWithAuth('/api/credits/me');

  if (!response.ok) {
    throw new Error('Failed to fetch credits');
  }

  const data = await response.json();
  return data.credits;
};

// Password Reset Functions
export const requestPasswordReset = async (email: string): Promise<void> => {
  const response = await fetch('/api/users/request-password-reset', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro ao solicitar recuperação de palavra-passe');
  }
};

export const resetPassword = async (resetToken: string, newPassword: string): Promise<void> => {
  const response = await fetch('/api/users/reset-password', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token: resetToken, newPassword }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erro ao redefinir palavra-passe');
  }
};
